import { createJSONStorage, type StateStorage } from 'zustand/middleware';

/**
 * Cross-platform storage adapter for Zustand persist middleware.
 *
 * - Web: Uses localStorage (synchronous)
 * - React Native: Uses AsyncStorage (async, requires @react-native-async-storage/async-storage)
 *
 * The adapter auto-detects the platform and uses the appropriate storage.
 */

// Minimal interface for localStorage-like storage (avoids DOM type dependency)
interface WebStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

// Type-safe check for React Native environment
function isReactNativeEnvironment(): boolean {
  try {
    const nav = (globalThis as { navigator?: { product?: string } }).navigator;
    return nav?.product === 'ReactNative';
  } catch {
    return false;
  }
}

// Type-safe localStorage getter for web
function getLocalStorage(): WebStorage | null {
  try {
    const storage = (globalThis as { localStorage?: WebStorage }).localStorage;
    if (storage && typeof storage.getItem === 'function') {
      return storage as WebStorage;
    }
  } catch {
    // localStorage not available
  }
  return null;
}

// Cache the environment check
const isReactNative = isReactNativeEnvironment();

// Lazy-loaded AsyncStorage for React Native (to avoid import errors on web)
let asyncStorageAdapter: StateStorage | null = null;

async function getAsyncStorage(): Promise<StateStorage> {
  if (!asyncStorageAdapter) {
    // Dynamic import to avoid bundling issues on web
    const AsyncStorage = await import('@react-native-async-storage/async-storage');
    const storage = AsyncStorage.default;

    asyncStorageAdapter = {
      getItem: async (name: string) => {
        const value = await storage.getItem(name);
        return value ?? null;
      },
      setItem: async (name: string, value: string) => {
        await storage.setItem(name, value);
      },
      removeItem: async (name: string) => {
        await storage.removeItem(name);
      },
    };
  }
  return asyncStorageAdapter;
}

/**
 * Raw storage adapter that works on both web and React Native.
 */
const rawStorage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    if (isReactNative) {
      const storage = await getAsyncStorage();
      return storage.getItem(name);
    }
    // Web: use localStorage
    const localStorage = getLocalStorage();
    if (localStorage) {
      return localStorage.getItem(name);
    }
    return null;
  },
  setItem: async (name: string, value: string): Promise<void> => {
    if (isReactNative) {
      const storage = await getAsyncStorage();
      await storage.setItem(name, value);
      return;
    }
    // Web: use localStorage
    const localStorage = getLocalStorage();
    if (localStorage) {
      localStorage.setItem(name, value);
    }
  },
  removeItem: async (name: string): Promise<void> => {
    if (isReactNative) {
      const storage = await getAsyncStorage();
      await storage.removeItem(name);
      return;
    }
    // Web: use localStorage
    const localStorage = getLocalStorage();
    if (localStorage) {
      localStorage.removeItem(name);
    }
  },
};

/**
 * Cross-platform storage for zustand persist middleware.
 * Wraps the raw storage with JSON serialization.
 */
export const crossPlatformStorage = createJSONStorage(() => rawStorage);
